import type { AssistantPageId } from './contracts.js';

export interface AssistantUiElementKnowledge {
  readonly id: string;
  readonly selector: string;
  readonly label: string;
  readonly behavior: string;
}

export interface AssistantPageKnowledge {
  readonly title: string;
  readonly purpose: string;
  readonly elements: readonly AssistantUiElementKnowledge[];
  readonly suggestions: readonly string[];
}

export const ASSISTANT_PAGE_KNOWLEDGE: Readonly<
Record<AssistantPageId, AssistantPageKnowledge>
> = Object.freeze({
  lobby: {
    title: 'Главный экран',
    purpose: 'Выбор ника, поиск соперника или запуск тренировочной игры с ботом.',
    elements: [
      {
        id: 'nickname',
        selector: '[data-testid="nickname-input"]',
        label: 'Поле ника',
        behavior: 'Задаёт отображаемое имя. Для аккаунта имя может быть только для чтения.',
      },
      {
        id: 'find-opponent',
        selector: '[data-testid="find-opponent"]',
        label: 'Найти соперника',
        behavior: 'Сохраняет локальный ник при необходимости и запускает поиск живого соперника.',
      },
      {
        id: 'play-bot',
        selector: '[data-testid="play-bot"]',
        label: 'Играть с ботом',
        behavior: 'Запускает одиночную тренировку с выбранной колодой.',
      },
      {
        id: 'header-decks',
        selector: '[data-onboarding="header-decks"]',
        label: 'Раздел колод',
        behavior: 'Открывает библиотеку пользовательских колод.',
      },
    ],
    suggestions: [
      'Как начать первую игру?',
      'Где посмотреть все карты?',
      'Чем игра с ботом отличается от поиска соперника?',
    ],
  },
  matchmaking: {
    title: 'Поиск соперника',
    purpose: 'Ожидание пары перед выбором колоды и стартом матча.',
    elements: [
      {
        id: 'cancel-search',
        selector: '[data-testid="cancel-search"]',
        label: 'Отменить поиск',
        behavior: 'Останавливает поиск и возвращает игрока на главный экран.',
      },
    ],
    suggestions: [
      'Что произойдёт, когда соперник найдётся?',
      'Как отменить поиск?',
    ],
  },
  deck_selection: {
    title: 'Выбор колоды перед матчем',
    purpose: 'Выбор готовой или сохранённой колоды и подтверждение готовности.',
    elements: [
      {
        id: 'deck-selection',
        selector: '[data-testid="deck-selection"], [data-testid="saved-deck-picker"]',
        label: 'Список колод',
        behavior: 'Показывает доступные колоды и их состав перед матчем.',
      },
      {
        id: 'lock-deck',
        selector: '[data-testid="lock-deck"], [data-testid="lock-saved-deck"]',
        label: 'Подтвердить колоду',
        behavior: 'Фиксирует выбранную колоду. После подтверждения сменить её в этой комнате нельзя.',
      },
    ],
    suggestions: [
      'Как выбрать колоду?',
      'Почему я не могу подтвердить выбор?',
      'Что будет после подтверждения?',
    ],
  },
  game: {
    title: 'Матч',
    purpose: 'Одновременное планирование карт, раскрытие планов и автоматическое разрешение боя.',
    elements: [
      {
        id: 'opponent-status',
        selector: '[data-onboarding="opponent-status"]',
        label: 'Состояние соперника',
        behavior: 'Показывает здоровье, ресурсы и известную публичную информацию соперника.',
      },
      {
        id: 'board',
        selector: '[data-onboarding="board"]',
        label: 'Игровое поле',
        behavior: 'Карты сражаются с картой напротив; при пустой встречной клетке урон проходит игроку.',
      },
      {
        id: 'player-resources',
        selector: '[data-onboarding="player-resources"]',
        label: 'Ресурсы игрока',
        behavior: 'Показывает здоровье, энергию, остаток колоды и дополнительные публичные ресурсы.',
      },
      {
        id: 'hand',
        selector: '[data-onboarding="hand"]',
        label: 'Рука',
        behavior: 'Содержит доступные карты. Карту можно нажать или перетащить для планирования.',
      },
      {
        id: 'plan',
        selector: '[data-onboarding="plan"]',
        label: 'План одноразовых карт',
        behavior: 'До готовности хранит скрыто запланированные заклинания, реакции и вирусы.',
      },
      {
        id: 'ready',
        selector: '[data-onboarding="ready"]',
        label: 'Кнопка готовности',
        behavior: 'Завершает планирование. Когда готовы оба игрока, планы раскрываются и разрешаются.',
      },
      {
        id: 'event-log',
        selector: '[data-testid="event-log"]',
        label: 'Журнал событий',
        behavior: 'Показывает публичную последовательность уже произошедших игровых событий.',
      },
    ],
    suggestions: [
      'Что мне делать в фазе планирования?',
      'Как разыграть карту из руки?',
      'Что означает механика на моей карте?',
      'Когда начинается бой?',
    ],
  },
  catalog: {
    title: 'Каталог карт',
    purpose: 'Просмотр игровых карт, характеристик и точных описаний механик.',
    elements: [
      {
        id: 'catalog-search',
        selector: '[data-testid="lab-search"]',
        label: 'Поиск по каталогу',
        behavior: 'Ищет по названию, id, типу и описаниям механик.',
      },
      {
        id: 'catalog-type',
        selector: '[data-testid="lab-type-filter"]',
        label: 'Фильтр типа',
        behavior: 'Оставляет в каталоге карты выбранного типа.',
      },
      {
        id: 'catalog-cards',
        selector: '.lab-card-grid',
        label: 'Карты каталога',
        behavior: 'Показывает полное представление карт и раскрываемые описания механик.',
      },
    ],
    suggestions: [
      'Найди карту, которая подходит под моё описание.',
      'Объясни механику этой карты и как раскрыть её описание.',
      'Что означают типы карт?',
      'Где собирать собственную колоду?',
    ],
  },
  sandbox: {
    title: 'Песочница',
    purpose: 'Свободная расстановка карт и ресурсов, поиск по каталогу и прогон получившейся игровой ситуации.',
    elements: [
      {
        id: 'sandbox-board',
        selector: '[data-testid="sandbox-board"]',
        label: 'Игровое поле песочницы',
        behavior: 'Содержит 18 клеток: три стола, три линии и две стороны. UNIT и STRUCTURE можно размещать на свободных клетках.',
      },
      {
        id: 'sandbox-search',
        selector: '[data-testid="sandbox-search"]',
        label: 'Поиск карт песочницы',
        behavior: 'Фильтрует доступные карты по названию, id и описанию.',
      },
      {
        id: 'sandbox-catalog',
        selector: '[data-testid="sandbox-catalog-grid"]',
        label: 'Каталог карт песочницы',
        behavior: 'Карту можно выбрать или перенести на поле; одноразовые карты попадают в подходящую зону стороны.',
      },
      {
        id: 'sandbox-run',
        selector: '[data-testid="sandbox-run-round"]',
        label: 'Провести раунд',
        behavior: 'Запускает построенную ситуацию тем же движком и правилами, что обычный матч.',
      },
      {
        id: 'sandbox-clear',
        selector: '[data-testid="sandbox-clear"]',
        label: 'Очистить сценарий',
        behavior: 'После подтверждения полностью очищает текущую расстановку.',
      },
    ],
    suggestions: [
      'Выстрой на поле симметричную ситуацию для проверки боя.',
      'Поставь дешёвых бойцов напротив прочной постройки.',
      'Найди карту по косвенному описанию.',
      'Добавь к текущей ситуации карту с посмертным эффектом.',
    ],
  },
  onboarding_deck: {
    title: 'Подготовка первой колоды',
    purpose: 'Автоматическая выдача базовой учебной колоды перед первой тренировкой с ботом.',
    elements: [
      {
        id: 'onboarding-starter',
        selector: '[data-testid="onboarding-starter-provisioning"]',
        label: 'Подготовка учебной колоды',
        behavior: 'Сервер сохраняет готовый «Учебный отряд» без выбора и затем запускает тренировку.',
      },
    ],
    suggestions: [
      'Какую колоду я получу?',
      'Какие карты входят в учебную колоду?',
      'Что будет в первом тренировочном бою?',
    ],
  },
  deck_library: {
    title: 'Библиотека колод',
    purpose: 'Создание, открытие, архивирование и удаление пользовательских колод.',
    elements: [
      {
        id: 'deck-library',
        selector: '[data-testid="deck-library"]',
        label: 'Библиотека колод',
        behavior: 'Показывает активные и архивные колоды, их готовность и занятые места.',
      },
      {
        id: 'create-deck',
        selector: '.deck-button-primary',
        label: 'Создать колоду',
        behavior: 'Создаёт пустой черновик и открывает его в редакторе.',
      },
      {
        id: 'deck-tabs',
        selector: '.deck-tabs',
        label: 'Активные колоды и архив',
        behavior: 'Переключает список между действующими и архивными колодами.',
      },
    ],
    suggestions: [
      'Как создать новую колоду?',
      'Чем архивирование отличается от удаления?',
      'Почему колода не готова к игре?',
    ],
  },
  deck_editor: {
    title: 'Редактор колоды',
    purpose: 'Сборка колоды из 12 слотов, проверка состава и сохранение черновика.',
    elements: [
      {
        id: 'deck-name',
        selector: '.deck-name-label',
        label: 'Название колоды',
        behavior: 'Название от 1 до 60 допустимых символов.',
      },
      {
        id: 'deck-slots',
        selector: '[data-testid="deck-slot-grid"]',
        label: 'Слоты колоды',
        behavior: 'Показывает 12 слотов текущего состава; карту можно убрать кнопкой в её углу.',
      },
      {
        id: 'deck-validation',
        selector: '[data-testid="deck-validation"]',
        label: 'Проверка состава',
        behavior: 'Объясняет, готова ли колода к игре и какие ошибки нужно исправить.',
      },
      {
        id: 'deck-save',
        selector: '.deck-editor-actions .deck-button-primary',
        label: 'Сохранить',
        behavior: 'Сохраняет валидный состав как готовую колоду или допустимый неполный состав как черновик.',
      },
      {
        id: 'deck-catalog-search',
        selector: '.deck-catalog-controls input',
        label: 'Поиск карт',
        behavior: 'Фильтрует карты по названию и описанию.',
      },
      {
        id: 'deck-catalog-type',
        selector: '.deck-catalog-controls select',
        label: 'Фильтр типа карт',
        behavior: 'Оставляет в каталоге карты выбранного типа.',
      },
      {
        id: 'deck-catalog',
        selector: '[data-testid="deck-card-catalog"]',
        label: 'Каталог доступных карт',
        behavior: 'Кнопки плюс и минус добавляют или убирают одну копию карты.',
      },
    ],
    suggestions: [
      'Собери мне агрессивную колоду.',
      'Найди карту по эффекту или описанию.',
      'Объясни, как работает механика выбранной карты.',
      'Улучши текущую колоду, но сохрани её идею.',
      'Объясни, как пользоваться редактором.',
    ],
  },
  unknown: {
    title: 'Текущий экран',
    purpose: 'Экран без отдельного контекстного набора инструментов.',
    elements: [],
    suggestions: [
      'Как устроена игра?',
      'Где найти редактор колод?',
    ],
  },
});

export function assistantPageElement(
  page: AssistantPageId,
  elementId: string,
): AssistantUiElementKnowledge | undefined {
  return ASSISTANT_PAGE_KNOWLEDGE[page].elements.find(({ id }) => id === elementId);
}
